import React from 'react';
import './ProductListing.scss';

/**
 * ProductListing Component
 * Displays products in a list format (OBO style)
 * Used for final product listing pages before detail view
 * 
 * Matches OBO pattern:
 * - Image 4: Product listing with list view showing products with images
 */
const ProductListing = ({ 
  products = [], 
  productId, 
  currentPath = [], 
  onProductClick 
}) => {
  // Use same-origin on live so product images load
  const API_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    ? window.location.origin
    : (process.env.REACT_APP_API_URL || 'http://localhost:3005').replace(/\/api\/?$/, '').replace(/\/+$/, '');

  // Helper to normalize path (remove double slashes)
  const normalizePath = (base, path) => {
    const baseUrl = (base || '').replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  const handleClick = (product) => {
    if (onProductClick) {
      onProductClick(product.id);
    } else {
      const path = currentPath.join(',');
      window.location.href = normalizePath(process.env.PUBLIC_URL, `/product-details/${productId}?nodeId=${product.id}&path=${path}`);
    }
  };

  if (products.length === 0) {
    return (
      <div className="product-listing-empty">
        <p>No products found.</p>
      </div>
    );
  }

  return (
    <div className="product-listing">
      <div className="product-listing-header">
        <div className="listing-count">
          {products.length} Article{products.length !== 1 ? 's' : ''} 
          {products.some(p => p.metadata?.variants) && (
            <span className="variants-count">
              ({products.reduce((sum, p) => sum + (p.metadata?.variants?.length || 1), 0)} Variants)
            </span>
          )}
        </div>
        <button className="filter-sort-btn">
          Filter & sorting
        </button>
      </div>

      <div className="product-listing-items">
        {products.map((product) => {
          const metadata = product.metadata || {};
          // Support both metadata.images and direct images array
          const images = Array.isArray(metadata.images)
            ? metadata.images
            : (Array.isArray(product.images) ? product.images : []);
          const firstImageEntry = images.length > 0 ? images[0] : null;
          let firstImage = null;
          let firstAlt = product.title;
          if (typeof firstImageEntry === 'string') {
            firstImage = firstImageEntry.startsWith('/uploads/')
              ? `${API_URL}${firstImageEntry}`
              : firstImageEntry;
          } else if (firstImageEntry && typeof firstImageEntry === 'object') {
            const raw = firstImageEntry.url || firstImageEntry.path || '';
            if (raw) {
              firstImage = raw.startsWith('/uploads/') ? `${API_URL}${raw}` : raw;
            }
            firstAlt = firstImageEntry.alt || firstAlt;
          }
          const variants = metadata.variants || [];
          const variantCount = variants.length || 1;

          return (
            <div 
              key={product.id} 
              className="product-listing-item"
              onClick={() => handleClick(product)}
            >
              <div className="listing-item-image">
                {firstImage ? (
                  <img 
                    src={firstImage} 
                    alt={firstAlt || product.title}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                ) : (
                  <div className="listing-item-placeholder">No Image</div>
                )}
              </div>
              <div className="listing-item-content">
                <div className="listing-item-variants">
                  {variantCount} Variant{variantCount !== 1 ? 's' : ''}
                </div>
                <h3 className="listing-item-title">{product.title}</h3>
                {product.item_no && (
                  <p className="listing-item-meta">Item: {product.item_no}</p>
                )}
                {product.type && (
                  <p className="listing-item-meta">Type: {product.type}</p>
                )}
              </div>
              <div className="listing-item-arrow">→</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductListing;
